import IconSearch from '@apollo/icons/default/IconSearch.svg';
import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import { LocationDescriptorObject } from 'history';
import _ from 'lodash';
import React from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { AnyRouteConfig } from 'src/lib/routeConfig/RouteConfig';

const FeatureAdvertisementDot = ({ collapsed }: { collapsed: boolean }) => (
  <div
    className={classnames(
      'absolute size-2 rounded bg-notice',
      collapsed ? '-right-1 -top-1' : 'right-1',
    )}
  />
);

function usePatchedRef<El>(ref: React.ForwardedRef<El>) {
  return React.useMemo(() => {
    if (ref && 'current' in ref) {
      return (el: El | null) => {
        ref.current = el; // eslint-disable-line no-param-reassign
      };
    } else {
      return ref;
    }
  }, [ref]);
}

const LinkComponent = React.forwardRef<
  unknown,
  (
    | { to: LocationDescriptorObject<unknown>; onClick?: undefined }
    | { onClick: () => void; to?: undefined }
  ) & {
    className: string;
    children: React.ReactNode;
    missingPermission: boolean;
  }
>(({ missingPermission, ...props }, ref) => {
  const patchedRef = usePatchedRef(ref);
  if (props.to && !missingPermission) {
    return <Link ref={patchedRef} {...props} />;
  } else {
    return <button type="button" ref={patchedRef} {...props} />;
  }
});

export const CollapsibleNavItem = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  (
    | { to: LocationDescriptorObject<unknown>; onClick?: undefined }
    | { onClick: () => void; to?: undefined }
  ) & {
    className?: string;
    label: string;
    Icon: typeof IconSearch;
    extraActiveRoutes?: AnyRouteConfig[];
    missingPermission?: boolean;
    collapsed: boolean;
    withFeatureAdvertisementDot?: boolean;
  }
>(
  (
    {
      className,
      label,
      Icon,
      extraActiveRoutes,
      missingPermission,
      collapsed,
      withFeatureAdvertisementDot,
      to,
      onClick,
    },
    ref,
  ) => {
    const patchedRef = usePatchedRef(ref);
    const location = useLocation();
    const match = to?.pathname
      ? matchPath(location.pathname, to.pathname)
      : false;
    const isActive = !!(
      match ||
      extraActiveRoutes?.some((route) => {
        return matchPath(location.pathname, {
          path: route.definition,
          exact: false,
        });
      })
    );

    return (
      <Tooltip
        placement="right"
        disabled={!missingPermission}
        label={
          <>
            You do not have permission to access the <b>{label}</b> page.
          </>
        }
      >
        {collapsed ? (
          <LinkComponent
            missingPermission={missingPermission ?? false}
            ref={ref}
            {...(to ? { to } : { onClick })}
            className={classnames(
              className,
              'group relative flex flex-col items-center gap-1 text-center text-[0.65rem] leading-tight text-primary',
              missingPermission ? 'cursor-not-allowed' : '',
            )}
          >
            <div
              className={classnames(
                'relative flex size-[24px] items-center justify-center rounded',
                missingPermission
                  ? 'bg-disabled'
                  : 'group-hover:bg-btn-secondary-hover',
                isActive && !missingPermission && 'bg-btn-secondary-hover',
              )}
            >
              {withFeatureAdvertisementDot && (
                <FeatureAdvertisementDot collapsed={collapsed} />
              )}
              <Icon
                className={classnames(
                  'size-4',
                  missingPermission ? 'text-icon-disabled' : '',
                )}
              />
            </div>
            {label}
          </LinkComponent>
        ) : (
          <Button
            {...(to ? { as: Link, to } : { onClick })}
            className={classnames(
              className,
              'relative h-11 justify-start font-normal',
              missingPermission &&
                'cursor-not-allowed bg-disabled text-disabled hover:bg-disabled',
              isActive && !missingPermission && 'bg-btn-secondary-hover',
            )}
            variant="hidden"
            aria-current={isActive ? 'page' : undefined}
            ref={patchedRef}
            isActive={isActive}
            leftIcon={
              <Icon className={missingPermission ? '!text-disabled' : ''} />
            }
            size="sm"
          >
            {withFeatureAdvertisementDot && (
              <FeatureAdvertisementDot collapsed={collapsed} />
            )}
            {label}
          </Button>
        )}
      </Tooltip>
    );
  },
);
